// Static seed data — stands in for the team-achievement + teams endpoints until
// the backend lands. Shapes mirror the eventual DTOs so the service swap is trivial.

import {
  ACHIEVEMENT_TIERS,
  achievementMedalDataUri,
  type TAchievementTier,
} from '@/core/utils/achievement-medal'
import type { TAchievementUnlockRule } from '@/core/utils/achievement-unlock'
import type {
  TAchievementTeam,
  TTeamAchievement,
} from '@/modules/team-achievement/team-achievement.types'

const TIER_ORDER: TAchievementTier[] = ['silver', 'gold', 'diamond']
const LEVELS = [1, 2, 3, 4]

// Team correct-answer thresholds per (tier, level) — a team pools its members'
// answers, so these sit an order of magnitude above the individual ladder.
const CORRECT_ANSWER_THRESHOLDS: Record<TAchievementTier, number[]> = {
  silver: [500, 1500, 3000, 5000],
  gold: [7500, 12000, 20000, 30000],
  diamond: [50000, 75000, 120000, 200000],
}

// Championship requirement stacked on the level-4 badge of each tier, so the
// multi-rule (AND) rendering has a fixture to exercise.
const LEAGUE_TITLE_THRESHOLDS: Record<TAchievementTier, number> = {
  silver: 1,
  gold: 3,
  diamond: 5,
}

// Start existing badges with the category-scoped correct-answer metric. The
// category intentionally stays empty so the admin selects a live backend
// category while editing instead of persisting a hardcoded category id.
function seedUnlockRules(
  tier: TAchievementTier,
  level: number
): TAchievementUnlockRule[] {
  const rules: TAchievementUnlockRule[] = [
    {
      id: `${tier}-${level}-correct-answers`,
      metric: 'category_correct_answers',
      category_id: null,
      operator: 'gte',
      value: CORRECT_ANSWER_THRESHOLDS[tier][level - 1] ?? 500,
    },
  ]
  if (level === 4) {
    rules.push({
      id: `${tier}-${level}-league-titles`,
      metric: 'league_championships',
      category_id: null,
      operator: 'gte',
      value: LEAGUE_TITLE_THRESHOLDS[tier],
    })
  }
  return rules
}

const DAYS = ['05', '12', '19', '26']

export const TEAM_ACHIEVEMENTS_SEED: TTeamAchievement[] = TIER_ORDER.flatMap(
  (tier, tierIdx) =>
    LEVELS.map((level) => {
      const label = ACHIEVEMENT_TIERS[tier].label
      const month = String(3 + tierIdx).padStart(2, '0') // 03..05
      return {
        id: `${tier}-${level}`,
        name: `${label} ${level}`,
        description: `${label} seviye ${level} takım başarımı. Bu rozeti kazanmak için takımın ilgili koşulları tamamla.`,
        image: achievementMedalDataUri(tier),
        tier,
        unlock_rules: seedUnlockRules(tier, level),
        acquired_at: `2026-${month}-${DAYS[level - 1]}T09:30:00.000Z`,
        is_active: !(tier === 'diamond' && level >= 3),
      }
    })
)

// Demo team pool for the detail screen (earned = did this team earn the badge).
const TEAM_POOL = [
  { id: 't1', name: 'Bilgi Avcıları', logo: 'system_team_1' },
  { id: 't2', name: 'Zeka Küpü', logo: 'system_team_2' },
  { id: 't3', name: 'Kelime Ustaları', logo: 'system_team_3' },
  { id: 't4', name: 'Şampiyonlar Ligi', logo: 'system_team_4' },
  { id: 't5', name: 'Beyin Fırtınası', logo: 'system_team_5' },
  { id: 't6', name: 'Cevap Makinesi', logo: 'system_team_6' },
]

// Deterministic per-achievement earned flags so the detail feels populated.
export function teamsForAchievement(id: string): TAchievementTeam[] {
  const seed = [...id].reduce((sum, ch) => sum + ch.charCodeAt(0), 0)
  return TEAM_POOL.map((team, index) => ({
    ...team,
    earned: (seed + index) % 2 === 0,
  }))
}
